import { useQuery } from '@tanstack/react-query';
import { ModalActions } from '@ui-kit/components/Modal/Modal';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import EmailInput from '@/components/EmailInput/EmailInput';
import MailIcon from '@/components/icons/MailIcon';
import type { ContactVerificationSuccessPayload } from '@/components/ModalRenderer/components/SE/contactVerificationSuccess';
import useOnContactVerificationSuccess from '@/components/ModalRenderer/components/SE/useOnContactVerificationSuccess';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import useModal from '@/hooks/useModal/useModal';
import useRequestEmailVerificationCode from '@/hooks/useRequestEmailVerificationCode';
import { meQueryOptions } from '@/queries/meQuery/meQuery';
import Button from '@/ui-kit/components/Button/Button';

type FormValues = { email: string };
type Payload = ContactVerificationSuccessPayload;

const EmailVerification = ({ closeModal, payload: { successAction } }: ModalProps<Payload>) => {
  const { t } = useTranslation(['Modal']);
  const { openModal } = useModal();
  const handleSuccessAction = useOnContactVerificationSuccess(successAction);

  const { data: me, isPending: isMeLoading } = useQuery(meQueryOptions());

  const { register, watch, setValue, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: { email: '' },
    values: me?.emailStatus === 'NOT_VERIFIED' ? { email: me.email } : undefined,
    resetOptions: { keepDirtyValues: true },
  });

  const { requestEmailVerificationCode, isPending, errorMessage } = useRequestEmailVerificationCode({
    onSuccess: ({ email, resendAfter }) => {
      openModal('SE.EMAIL_VERIFICATION_OTP', {
        payload: { email, resendAfter, successAction },
      });
    },
    onAlreadyVerified: () => {
      closeModal();
      handleSuccessAction();
    },
  });

  return (
    // TODO: Revisit empty Modal title + custom heading layout (shared with MobileVerification).
    <Modal title="">
      <div className="-mt-2 mb-6 flex flex-col items-center text-center">
        <MailIcon className="text-icon-brand mb-4 size-12" aria-hidden="true" />
        <h2 className="text-heading mb-2 lg:text-center">{t('Modal:EmailVerification.title')}</h2>
        <p className="text-text-subtle">{t('Modal:EmailVerification.subtitle')}</p>
      </div>
      <form
        className="flex flex-col gap-4"
        onSubmit={e => handleSubmit(({ email }) => requestEmailVerificationCode(email))(e)}
      >
        <EmailInput<FormValues>
          register={register}
          watch={watch}
          setValue={value => setValue('email', value)}
          formState={formState}
          name="email"
          label={t('Modal:EmailVerification.emailLabel')}
          required
          disabled={isPending || isMeLoading}
          errorMessage={errorMessage}
          enterKeyHint="go"
        />
        <ModalActions>
          <Button type="submit" loading={isPending}>
            {t('Modal:EmailVerification.submit')}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
};

export default EmailVerification;
